import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const EMPTY = { name: "", sku: "", item_type: "raw", unit: "pcs", image_url: "", components: [], stages: [], stock_quantity: "0", min_stock: "0", supplier: "", category: "", lead_time_days: "0", pack_size: "0", moq: "0", line_id: "none" };
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
let keySeq = 0;
const nextKey = () => `comp-${++keySeq}`;
const nonNeg = (v) => Math.max(0, Number(v) || 0);

function fromItem(item) {
  return {
    name: item.name,
    sku: item.sku,
    item_type: item.item_type,
    unit: item.unit,
    image_url: item.image_url || "",
    stock_quantity: String(item.stock_quantity ?? 0),
    min_stock: String(item.min_stock ?? 0),
    supplier: item.supplier || "",
    category: item.category || "",
    lead_time_days: String(item.lead_time_days ?? 0),
    pack_size: String(item.pack_size ?? 0),
    moq: String(item.moq ?? 0),
    line_id: item.line_id || "none",
    components: (item.components || []).map((c) => ({ ...c, _key: nextKey() })),
    stages: (item.stages || []).map((st) => ({ name: st.name, type: st.type || "in_house", _key: nextKey() })),
  };
}

// API payload: raw-only sourcing fields are nulled for manufactured items and vice-versa.
export function buildBomPayload(form, isEdit) {
  // Manufactured goods AND manufactured spare parts both carry a recipe + stage pipeline
  // and are made in-house (no purchasing/sourcing fields).
  const mfg = form.item_type === "manufactured" || form.item_type === "mfg_spare";
  const payload = {
    ...form,
    image_url: form.image_url || null,
    supplier: mfg ? null : (form.supplier || null),
    category: mfg ? null : (form.category || null),
    min_stock: nonNeg(form.min_stock),
    lead_time_days: mfg ? 0 : nonNeg(form.lead_time_days),
    pack_size: mfg ? 0 : nonNeg(form.pack_size),
    moq: mfg ? 0 : nonNeg(form.moq),
    line_id: mfg && form.line_id !== "none" ? form.line_id : null,
    components: mfg ? form.components.filter((c) => c.item_id).map((c) => ({ item_id: c.item_id, quantity_per_unit: Number(c.quantity_per_unit) || 1 })) : [],
    stages: mfg ? form.stages.filter((st) => (st.name || "").trim()).map((st) => ({ name: st.name.trim(), type: st.type || "in_house" })) : [],
  };
  if (isEdit) delete payload.stock_quantity; // edits never touch stock — use the Adjust Stock dialog
  else payload.stock_quantity = nonNeg(form.stock_quantity);
  return payload;
}

function fromTemplate(t) {
  const f = fromItem(t);
  return { ...f, sku: `${f.sku}-COPY`, name: `${f.name} (Copy)`, stock_quantity: "0", image_url: "" };
}

export function useBomForm({ open, item, template, items, suppliers, onSubmit, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (item) setForm(fromItem(item));
    else setForm(template ? fromTemplate(template) : EMPTY);
  }, [open, item, template, setForm]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateRow = (list, i, k, v) => setForm((f) => ({ ...f, [list]: f[list].map((row, idx) => (idx === i ? { ...row, [k]: v } : row)) }));
  const addRow = (list, row) => setForm((f) => ({ ...f, [list]: [...f[list], { ...row, _key: nextKey() }] }));
  const removeRow = (list, i) => setForm((f) => ({ ...f, [list]: f[list].filter((_, idx) => idx !== i) }));
  const moveRow = (list, from, to) => setForm((f) => {
    if (to < 0 || to >= f[list].length) return f;
    const rows = [...f[list]];
    const [row] = rows.splice(from, 1);
    rows.splice(to, 0, row);
    return { ...f, [list]: rows };
  });

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > MAX_IMAGE_BYTES) { toast.error("Image too large — please use one under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("image_url", reader.result);
    reader.onerror = () => toast.error("Could not read that image");
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const supplierNames = useMemo(() => (suppliers || []).filter((s) => s.active !== false).map((s) => s.name), [suppliers]);
  const supplierOptions = useMemo(() => [
    { value: "__none__", label: "— None —" },
    ...(form.supplier && !supplierNames.includes(form.supplier) ? [{ value: form.supplier, label: `${form.supplier} (current)` }] : []),
    ...supplierNames.map((n) => ({ value: n, label: n })),
  ], [form.supplier, supplierNames]);
  const candidateOptions = useMemo(() => items
    .filter((i) => (!item || i.id !== item.id) && (i.approval_status || "Active") === "Active")
    .map((it) => ({ value: it.id, label: `${it.name} (${it.sku})${it.item_type === "manufactured" ? " · manufactured" : ""}`, keywords: `${it.name} ${it.sku}` })),
  [items, item]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(buildBomPayload(form, !!item), item);
      onOpenChange(false);
    } catch (err) {
      logger.error("BOM save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return { form, set, saving, submit, handleFile, updateRow, addRow, removeRow, moveRow, supplierOptions, candidateOptions };
}
